import api from './api';
import { locationStorage } from './locationStorage';

export type WebTrackingTarget = { osId: number; faseId: number };

interface Posicao {
  lat: number;
  lng: number;
  velocidade?: number;
  direcao?: number;
  precisao?: number;
  altitude?: number;
  obtidaEm: number;
}

interface WebTrackingCallbacks {
  onLocation?: (lat: number, lng: number) => void;
  onError?: (message: string | null) => void;
}

/** Intervalo mínimo entre envios para cada fase. */
const INTERVALO_ENVIO_MS = 15000;
/** Posição mais antiga que isso não é reenviada pelo envio periódico. */
const POSICAO_MAX_IDADE_MS = 120000;
const MAX_TENTATIVAS_PENDENTE = 5;

let alvos: WebTrackingTarget[] = [];
let watchId: number | null = null;
let timerEnvio: number | null = null;
let ultimaPosicao: Posicao | null = null;
let ultimoEnvioEm = 0;
let enviando = false;
let wakeLock: { release: () => Promise<void> } | null = null;
let callbacks: WebTrackingCallbacks = {};
let listenersRegistrados = false;

const erroDefinitivo = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return typeof status === 'number' && status >= 400 && status < 500 && status !== 408 && status !== 429;
};

const payloadDe = (p: Omit<Posicao, 'obtidaEm'>) => ({
  lat: p.lat,
  lng: p.lng,
  velocidade: p.velocidade || 0,
  direcao: p.direcao || 0,
  precisao: p.precisao || 0,
  altitude: p.altitude || 0,
});

const solicitarWakeLock = async (): Promise<void> => {
  if (wakeLock || document.hidden || !('wakeLock' in navigator)) return;
  try {
    const sentinel = await (navigator as unknown as {
      wakeLock: { request: (tipo: 'screen') => Promise<{ release: () => Promise<void>; addEventListener: (ev: 'release', cb: () => void) => void }> };
    }).wakeLock.request('screen');
    wakeLock = sentinel;
    sentinel.addEventListener('release', () => {
      wakeLock = null;
    });
  } catch (err) {
    console.warn('Wake Lock indisponível:', err);
  }
};

const liberarWakeLock = async (): Promise<void> => {
  const atual = wakeLock;
  wakeLock = null;
  if (atual) {
    try {
      await atual.release();
    } catch {
      /* ignore */
    }
  }
};

const enviarParaAlvo = async (alvo: WebTrackingTarget, posicao: Posicao): Promise<void> => {
  try {
    await api.put(
      `/rastreamento/${alvo.osId}/fases/${alvo.faseId}/atualizar-posicao`,
      payloadDe(posicao)
    );
  } catch (error) {
    if (erroDefinitivo(error)) return;
    try {
      await locationStorage.savePendingLocation({ ...alvo, ...payloadDe(posicao) });
    } catch (storageError) {
      console.error('Erro ao salvar localização pendente:', storageError);
    }
  }
};

const enviarPosicao = async (posicao: Posicao): Promise<void> => {
  if (enviando || alvos.length === 0) return;
  enviando = true;
  ultimoEnvioEm = Date.now();
  try {
    await Promise.all(alvos.map((alvo) => enviarParaAlvo(alvo, posicao)));
  } finally {
    enviando = false;
  }
};

const sincronizarPendentes = async (): Promise<void> => {
  try {
    const pendentes = await locationStorage.getPendingLocations();
    pendentes.sort((a, b) => a.timestamp - b.timestamp);
    for (const pendente of pendentes) {
      if (pendente.retryCount > MAX_TENTATIVAS_PENDENTE) {
        await locationStorage.removePendingLocation(pendente.id);
        continue;
      }
      try {
        await api.put(
          `/rastreamento/${pendente.osId}/fases/${pendente.faseId}/atualizar-posicao`,
          payloadDe(pendente)
        );
        await locationStorage.removePendingLocation(pendente.id);
      } catch (error) {
        if (erroDefinitivo(error)) {
          await locationStorage.removePendingLocation(pendente.id);
        } else {
          await locationStorage.incrementRetryCount(pendente.id);
          break;
        }
      }
    }
  } catch (error) {
    console.error('Erro ao sincronizar localizações pendentes:', error);
  }
};

const aoReceberPosicao = (position: GeolocationPosition) => {
  ultimaPosicao = {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    velocidade: position.coords.speed != null ? position.coords.speed * 3.6 : undefined,
    direcao: position.coords.heading ?? undefined,
    precisao: position.coords.accuracy ?? undefined,
    altitude: position.coords.altitude ?? undefined,
    obtidaEm: Date.now(),
  };
  callbacks.onError?.(null);
  callbacks.onLocation?.(ultimaPosicao.lat, ultimaPosicao.lng);

  if (Date.now() - ultimoEnvioEm >= INTERVALO_ENVIO_MS) {
    void enviarPosicao(ultimaPosicao);
  }
};

const aoFalharPosicao = (error: GeolocationPositionError) => {
  if (error.code === error.PERMISSION_DENIED) {
    callbacks.onError?.(
      'Permissão de localização negada. Ative a localização do navegador para este site.'
    );
  } else if (error.code === error.POSITION_UNAVAILABLE) {
    callbacks.onError?.('Localização indisponível no momento. Tentando novamente...');
  }
};

/** Envio periódico mesmo com o veículo parado (watchPosition só dispara quando há mudança). */
const envioPeriodico = () => {
  if (!ultimaPosicao || Date.now() - ultimaPosicao.obtidaEm > POSICAO_MAX_IDADE_MS) return;
  if (Date.now() - ultimoEnvioEm >= INTERVALO_ENVIO_MS) {
    void enviarPosicao(ultimaPosicao);
  }
};

const registrarListeners = () => {
  if (listenersRegistrados) return;
  listenersRegistrados = true;
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && alvos.length > 0) {
      void solicitarWakeLock();
      void sincronizarPendentes();
    }
  });
  window.addEventListener('online', () => {
    if (alvos.length > 0) void sincronizarPendentes();
  });
};

const iniciar = () => {
  if (watchId !== null) return;
  if (!navigator.geolocation) {
    callbacks.onError?.('Geolocalização não disponível neste navegador.');
    return;
  }
  registrarListeners();
  void locationStorage.init().catch(() => undefined);
  void solicitarWakeLock();
  watchId = navigator.geolocation.watchPosition(aoReceberPosicao, aoFalharPosicao, {
    enableHighAccuracy: true,
    timeout: 30000,
    maximumAge: 10000,
  });
  timerEnvio = window.setInterval(envioPeriodico, INTERVALO_ENVIO_MS);
  void sincronizarPendentes();
};

const parar = () => {
  if (watchId !== null) {
    navigator.geolocation.clearWatch(watchId);
    watchId = null;
  }
  if (timerEnvio !== null) {
    window.clearInterval(timerEnvio);
    timerEnvio = null;
  }
  ultimaPosicao = null;
  ultimoEnvioEm = 0;
  void liberarWakeLock();
  callbacks.onError?.(null);
};

export const webTracking = {
  configurar(novosCallbacks: WebTrackingCallbacks) {
    callbacks = novosCallbacks;
  },

  /** Define as fases que recebem a posição atual; lista vazia encerra o rastreamento. */
  atualizarAlvos(novosAlvos: WebTrackingTarget[]) {
    const entrouAlvoNovo = novosAlvos.some(
      (n) => !alvos.some((a) => a.osId === n.osId && a.faseId === n.faseId)
    );
    alvos = [...novosAlvos];
    if (alvos.length === 0) {
      parar();
      return;
    }
    iniciar();
    if (entrouAlvoNovo && ultimaPosicao) {
      void enviarPosicao(ultimaPosicao);
    }
  },

  sincronizarPendentes,
};
